import { z } from "zod";
import {
  commandContextSchema,
  commandError,
  type AuditEntry,
  type CommandContext,
  type CommandReceipt,
  type CommandResult,
  type CommandSuccess,
  type ImmutableActionReference,
  stableFingerprint,
  taskActionTypes,
  taskOrigins,
} from "../../lib/command-contract";
import type { ActivityEvent, Task, TaskStatus } from "../../lib/types";

const prioritySchema = z.enum(["low", "medium", "high"]);
const progressSchema = z.enum(["todo", "doing", "waiting"]);
const referenceSchema = z.object({
  type: z.string().trim().min(1),
  id: z.string().trim().min(1),
  version: z.number().int().positive(),
  runId: z.string().trim().min(1).optional(),
  runVersion: z.number().int().positive().optional(),
  stepId: z.string().trim().min(1).optional(),
  stepVersion: z.number().int().positive().optional(),
  blocking: z.boolean().optional(),
});

export const taskDraftSchema = z.object({
  /** Ausente na tarefa interna; presente em tudo que pertence a um cadastro. */
  leadId: z.string().trim().min(1).optional(),
  membershipId: z.string().trim().min(1).optional(),
  description: z.string().trim().min(1, "Descreva a ação").max(500),
  dueAt: z.string().datetime({ offset: true }),
  priority: prioritySchema,
  note: z.string().trim().max(2000).optional(),
  origin: z.enum(taskOrigins).default("human"),
  actionType: z.enum(taskActionTypes).default("manual_follow_up"),
  sensitive: z.boolean().default(false),
  immutableReference: referenceSchema.optional(),
  // O quadro cria a tarefa já dentro de uma coluna; a lista cria em "A fazer".
  progress: progressSchema.default("todo"),
});

export type TaskDraft = z.output<typeof taskDraftSchema>;
export type TaskDraftInput = z.input<typeof taskDraftSchema>;

export const taskTransitionSchema = z.object({
  taskId: z.string().trim().min(1, "Tarefa é obrigatória"),
});

export type TaskTransitionInput = z.infer<typeof taskTransitionSchema>;

export const taskProgressSchema = z.object({
  taskId: z.string().trim().min(1, "Tarefa é obrigatória"),
  progress: progressSchema,
});

export type TaskProgressInput = z.infer<typeof taskProgressSchema>;

/**
 * Edição do conteúdo da tarefa.
 *
 * O cadastro vinculado não entra: os eventos da jornada já registram esta
 * tarefa no lead de origem, e trocá-lo aqui reescreveria o passado (§7.4).
 */
export const taskEditSchema = z.object({
  taskId: z.string().trim().min(1, "Tarefa é obrigatória"),
  description: z.string().trim().min(1, "Descreva a ação").max(500),
  dueAt: z.string().datetime({ offset: true }),
  priority: prioritySchema,
  note: z.string().trim().max(2000).optional(),
});

export type TaskEditInput = z.infer<typeof taskEditSchema>;

export type TaskTransition =
  | "created"
  | "completed"
  | "reopened"
  | "progress_changed"
  | "edited"
  | "deleted";

export interface TaskCommandValue {
  task: Task;
  /**
   * Evento da jornada. Ausente na tarefa interna: jornada pertence a um lead
   * (§7.4), e uma tarefa sem cadastro não tem jornada onde aparecer. A
   * auditoria do comando continua igual nos dois casos.
   */
  event?: ActivityEvent;
  transition: TaskTransition;
}

export interface TaskCommandState {
  tasks: Task[];
  events: ActivityEvent[];
  commandReceipts: CommandReceipt[];
  auditLog: AuditEntry[];
  outboxJobs: import("../../lib/command-contract").OutboxJob[];
  leadIds?: readonly string[];
  membershipIds?: readonly string[];
}

export interface TaskCommandDeps {
  now: () => string;
  uuid: () => string;
}

export interface TaskCommandExecution {
  result: CommandResult<TaskCommandValue>;
  state: TaskCommandState;
}

type RuntimeRecord = Record<string, unknown>;

function asRecord(value: unknown): RuntimeRecord | undefined {
  try {
    if (value === null || typeof value !== "object" || Array.isArray(value)) return undefined;
    const prototype = Object.getPrototypeOf(value);
    if (prototype !== Object.prototype && prototype !== null) return undefined;
    if (Object.getOwnPropertySymbols(value).length > 0) return undefined;

    const normalized = Object.create(null) as RuntimeRecord;
    for (const key of Object.getOwnPropertyNames(value)) {
      const descriptor = Object.getOwnPropertyDescriptor(value, key);
      if (!descriptor?.enumerable || !("value" in descriptor)) return undefined;
      normalized[key] = descriptor.value;
    }
    return normalized;
  } catch {
    return undefined;
  }
}

function denseArray(value: unknown): unknown[] | undefined {
  try {
    if (!Array.isArray(value) || Object.getPrototypeOf(value) !== Array.prototype) return undefined;
    if (Object.getOwnPropertySymbols(value).length > 0) return undefined;

    const lengthDescriptor = Object.getOwnPropertyDescriptor(value, "length");
    if (
      !lengthDescriptor ||
      !("value" in lengthDescriptor) ||
      !Number.isSafeInteger(lengthDescriptor.value) ||
      lengthDescriptor.value < 0
    )
      return undefined;
    const length = lengthDescriptor.value;
    const ownNames = Object.getOwnPropertyNames(value);
    if (ownNames.length !== length + 1 || !ownNames.includes("length")) return undefined;
    for (const key of ownNames) {
      if (key === "length") continue;
      if (!/^(0|[1-9]\d*)$/.test(key) || Number(key) >= length) return undefined;
    }

    const normalized: unknown[] = [];
    for (let index = 0; index < length; index += 1) {
      const descriptor = Object.getOwnPropertyDescriptor(value, String(index));
      if (!descriptor?.enumerable || !("value" in descriptor)) return undefined;
      normalized.push(descriptor.value);
    }
    return normalized;
  } catch {
    return undefined;
  }
}

const INVALID_TASK_INPUT = Symbol("invalid_task_input");

function normalizeJsonValue(value: unknown, seen: WeakSet<object>): unknown | typeof INVALID_TASK_INPUT {
  try {
    if (value === null) return null;
    if (typeof value !== "object") {
      return typeof value === "function" || typeof value === "symbol" || typeof value === "bigint"
        ? INVALID_TASK_INPUT
        : value;
    }
    if (seen.has(value)) return INVALID_TASK_INPUT;
    seen.add(value);

    if (Array.isArray(value)) {
      const array = denseArray(value);
      if (!array) return INVALID_TASK_INPUT;
      const normalized: unknown[] = [];
      for (const item of array) {
        const child = normalizeJsonValue(item, seen);
        if (child === INVALID_TASK_INPUT) return INVALID_TASK_INPUT;
        normalized.push(child);
      }
      seen.delete(value);
      return normalized;
    }

    const record = asRecord(value);
    if (!record) return INVALID_TASK_INPUT;
    const normalized = Object.create(null) as RuntimeRecord;
    for (const key of Object.getOwnPropertyNames(record)) {
      const child = normalizeJsonValue(record[key], seen);
      if (child === INVALID_TASK_INPUT) return INVALID_TASK_INPUT;
      normalized[key] = child;
    }
    seen.delete(value);
    return normalized;
  } catch {
    return INVALID_TASK_INPUT;
  }
}

function normalizeTaskJson(value: unknown): unknown | undefined {
  const normalized = normalizeJsonValue(value, new WeakSet<object>());
  return normalized === INVALID_TASK_INPUT ? undefined : normalized;
}

function normalizeTaskProgress(task: Task): Task {
  // Tarefa gravada antes do quadro existir não tem andamento; "a fazer" é o
  // único default honesto, porque ninguém declarou outro.
  return task.progress ? task : { ...task, progress: "todo" };
}

function normalizeState(state: TaskCommandState): TaskCommandState {
  return {
    tasks: state.tasks.map(normalizeTaskProgress),
    events: [...state.events],
    commandReceipts: [...state.commandReceipts],
    auditLog: [...state.auditLog],
    outboxJobs: [...state.outboxJobs],
    leadIds: state.leadIds,
    membershipIds: state.membershipIds,
  };
}

function checkedContext(context: unknown): CommandContext | undefined {
  const normalized = normalizeTaskJson(context);
  if (normalized === undefined) return undefined;
  const parsed = commandContextSchema.safeParse(normalized);
  return parsed.success ? parsed.data : undefined;
}

const INVALID_TASK_CONTEXT: CommandContext = Object.freeze({
  ownerId: "invalid-context",
  actorType: "system",
  actorId: "task-command",
  origin: "system",
  commandId: "invalid-context",
  correlationId: "invalid-context",
  idempotencyKey: "invalid-context",
  requestedAt: "1970-01-01T00:00:00.000Z",
  schemaVersion: 1,
});

function invalidContextFailure(state: TaskCommandState): TaskCommandExecution {
  return {
    state,
    result: commandError(INVALID_TASK_CONTEXT, "validation_error", "Contexto de comando inválido.", { severity: "warning" }),
  };
}

function recordLookup(
  state: TaskCommandState,
  context: CommandContext,
  command: string,
  fingerprint: string,
): TaskCommandExecution | null {
  const receipt = state.commandReceipts.find(
    (item) =>
      item.ownerId === context.ownerId &&
      item.command === command &&
      item.idempotencyKey === context.idempotencyKey,
  );
  if (!receipt) return null;
  if (receipt.requestFingerprint !== fingerprint) {
    return {
      state,
      result: commandError(
        context,
        "idempotency_conflict",
        "A chave de idempotência já foi usada para outra intenção.",
        { severity: "warning" },
      ),
    };
  }
  if (!receipt.result.ok) {
    return { state, result: receipt.result as CommandResult<TaskCommandValue> };
  }
  return {
    state,
    result: {
      ...receipt.result,
      status: "replayed",
    } as CommandSuccess<TaskCommandValue>,
  };
}

function validationFailure(
  context: CommandContext,
  error: unknown,
): TaskCommandExecution {
  const message = error instanceof z.ZodError
    ? error.issues[0]?.message ?? "Dados da tarefa inválidos."
    : error instanceof Error
      ? error.message
      : "Dados da tarefa inválidos.";
  return {
    state: undefined as never,
    result: commandError(context, "validation_error", message, { severity: "warning" }),
  };
}

function notFoundFailure(
  context: CommandContext,
  message: string,
): TaskCommandExecution {
  return {
    state: undefined as never,
    result: commandError(context, "not_found", message, { severity: "warning" }),
  };
}

function invalidStateFailure(
  context: CommandContext,
  message: string,
): TaskCommandExecution {
  return {
    state: undefined as never,
    result: commandError(context, "invalid_state", message, { severity: "warning" }),
  };
}

function taskSnapshot(task: Task): Record<string, unknown> {
  return {
    id: task.id,
    leadId: task.leadId,
    membershipId: task.membershipId,
    description: task.description,
    dueAt: task.dueAt,
    priority: task.priority,
    status: task.status,
    progress: task.progress,
    note: task.note,
    completedAt: task.completedAt,
    version: task.version,
    origin: task.origin,
    actionType: task.actionType,
    sensitive: task.sensitive,
    immutableReference: task.immutableReference,
  };
}

function makeEvent(
  task: Task,
  context: CommandContext,
  transition: TaskTransition,
  now: string,
  eventId: string,
): ActivityEvent | undefined {
  if (!task.leadId) return undefined;
  const labels: Record<TaskTransition, string> = {
    created: "Tarefa criada",
    completed: "Tarefa concluída",
    reopened: "Tarefa reaberta",
    progress_changed: "Andamento da tarefa alterado",
    edited: "Tarefa editada",
    deleted: "Tarefa excluída",
  };
  return {
    id: eventId,
    leadId: task.leadId,
    membershipId: task.membershipId,
    taskId: task.id,
    type: `task_${transition}`,
    title: transition === "created" ? task.description : labels[transition],
    description: transition === "created" ? task.description : task.description,
    occurredAt: now,
    kind: "event",
    actorType: context.actorType,
    actorId: context.actorId,
    origin: context.origin,
    commandId: context.commandId,
    correlationId: context.correlationId,
    causationId: context.causationId,
    countsAsInteraction: false,
  };
}

function finish(
  state: TaskCommandState,
  context: CommandContext,
  command: string,
  fingerprint: string,
  task: Task,
  event: ActivityEvent | undefined,
  transition: TaskTransition,
  previousVersion: number,
  deps: TaskCommandDeps,
): TaskCommandExecution {
  // Sem evento, o instante do comando ainda precisa existir para auditoria e
  // recibo: ele vem do próprio andamento da tarefa.
  const occurredAt = event?.occurredAt ?? task.updatedAt;
  const result: CommandSuccess<TaskCommandValue> = {
    ok: true,
    status: "applied",
    value: { task, event, transition },
    commandId: context.commandId,
    correlationId: context.correlationId,
    version: task.version,
    eventIds: event ? [event.id] : [],
    outboxJobIds: [],
    auditId: deps.uuid(),
    warnings: [],
  };
  const audit: AuditEntry = {
    id: result.auditId,
    ownerId: context.ownerId,
    command,
    commandId: context.commandId,
    correlationId: context.correlationId,
    causationId: context.causationId,
    actorType: context.actorType,
    actorId: context.actorId,
    origin: context.origin,
    targetType: "task",
    targetId: task.id,
    idempotencyKey: context.idempotencyKey,
    requestFingerprint: fingerprint,
    result: "applied",
    occurredAt,
    expectedVersion: context.expectedVersion,
    previousVersion,
    nextVersion: task.version,
    before: transition === "created" ? undefined : taskSnapshot({ ...task, version: previousVersion }),
    after: taskSnapshot(task),
  };
  const receipt: CommandReceipt = {
    id: deps.uuid(),
    ownerId: context.ownerId,
    command,
    idempotencyKey: context.idempotencyKey,
    requestFingerprint: fingerprint,
    commandId: context.commandId,
    correlationId: context.correlationId,
    result,
    createdAt: occurredAt,
    schemaVersion: context.schemaVersion,
  };
  return {
    result,
    state: {
      ...state,
      tasks: transition === "created"
        ? [task, ...state.tasks]
        : state.tasks.map((item) => item.id === task.id ? task : item),
      events: event ? [...state.events, event] : state.events,
      commandReceipts: [...state.commandReceipts, receipt],
      auditLog: [...state.auditLog, audit],
    },
  };
}

function validReference(reference: ImmutableActionReference | undefined): boolean {
  return !reference || (reference.id.trim().length > 0 && reference.type.trim().length > 0 && reference.version > 0);
}

export function executeCreateTask(
  stateInput: TaskCommandState,
  input: TaskDraftInput,
  contextInput: CommandContext,
  deps: TaskCommandDeps,
): TaskCommandExecution {
  const context = checkedContext(contextInput);
  if (!context) return invalidContextFailure(stateInput);
  let draft: TaskDraft;
  try {
    draft = taskDraftSchema.parse(normalizeTaskJson(input));
  } catch (error) {
    return { ...validationFailure(context, error), state: stateInput };
  }
  const fingerprint = stableFingerprint(draft);
  const state = normalizeState(stateInput);
  const replay = recordLookup(state, context, "tasks.create", fingerprint);
  if (replay) return replay;
  if (draft.leadId && state.leadIds && !state.leadIds.includes(draft.leadId))
    return { ...notFoundFailure(context, "Lead não encontrado."), state: stateInput };
  // Participação é contexto comercial: sem cadastro ela não faz sentido.
  if (!draft.leadId && draft.membershipId)
    return {
      ...validationFailure(context, new Error("Tarefa interna não pertence a uma participação.")),
      state: stateInput,
    };
  if (draft.membershipId && state.membershipIds && !state.membershipIds.includes(draft.membershipId))
    return { ...notFoundFailure(context, "Participação não encontrada."), state: stateInput };
  if (!validReference(draft.immutableReference))
    return { ...validationFailure(context, new Error("Referência imutável inválida.")), state: stateInput };
  const now = deps.now();
  const task: Task = {
    id: deps.uuid(),
    leadId: draft.leadId,
    membershipId: draft.membershipId,
    description: draft.description,
    dueAt: draft.dueAt,
    priority: draft.priority,
    status: "pending",
    progress: draft.progress,
    note: draft.note || undefined,
    createdAt: now,
    updatedAt: now,
    version: 1,
    origin: draft.origin,
    actionType: draft.actionType,
    sensitive: draft.sensitive,
    immutableReference: draft.immutableReference,
  };
  const event = makeEvent(task, context, "created", now, deps.uuid());
  return finish(state, context, "tasks.create", fingerprint, task, event, "created", 0, deps);
}

function executeTransition(
  transition: Exclude<TaskTransition, "created">,
  stateInput: TaskCommandState,
  input: TaskTransitionInput,
  contextInput: CommandContext,
  deps: TaskCommandDeps,
): TaskCommandExecution {
  const context = checkedContext(contextInput);
  if (!context) return invalidContextFailure(stateInput);
  let parsed: TaskTransitionInput;
  try {
    parsed = taskTransitionSchema.parse(normalizeTaskJson(input));
  } catch (error) {
    return { ...validationFailure(context, error), state: stateInput };
  }
  const fingerprint = stableFingerprint({ taskId: parsed.taskId, expectedVersion: context.expectedVersion });
  const state = normalizeState(stateInput);
  const replay = recordLookup(state, context, `tasks.${transition}`, fingerprint);
  if (replay) return replay;
  const current = state.tasks.find((item) => item.id === parsed.taskId && !item.deletedAt);
  if (!current)
    return { ...notFoundFailure(context, "Tarefa não encontrada."), state: stateInput };
  const normalizedCurrent: Task = {
    ...current,
    createdAt: current.createdAt ?? current.dueAt,
    updatedAt: current.updatedAt ?? current.createdAt ?? current.dueAt,
    version: current.version ?? 1,
    origin: current.origin ?? "human",
    actionType: current.actionType ?? "manual_follow_up",
    sensitive: current.sensitive ?? false,
    progress: current.progress ?? "todo",
  };
  if (context.expectedVersion !== undefined && context.expectedVersion !== normalizedCurrent.version)
    return { state: stateInput, result: commandError(context, "version_conflict", "A tarefa foi alterada por outra operação.", { severity: "warning" }) };
  const expectedStatus: TaskStatus = transition === "completed" ? "pending" : "done";
  if (normalizedCurrent.status !== expectedStatus) {
    return {
      ...invalidStateFailure(
        context,
        transition === "completed" ? "Somente tarefas pendentes podem ser concluídas." : "Somente tarefas concluídas podem ser reabertas.",
      ),
      state: stateInput,
    };
  }
  const now = deps.now();
  const task: Task = {
    ...normalizedCurrent,
    status: transition === "completed" ? "done" : "pending",
    completedAt: transition === "completed" ? now : undefined,
    updatedAt: now,
    version: normalizedCurrent.version + 1,
  };
  const event = makeEvent(task, context, transition, now, deps.uuid());
  const execution = finish(state, context, `tasks.${transition}`, fingerprint, task, event, transition, normalizedCurrent.version, deps);
  const audit = execution.state.auditLog.at(-1);
  if (audit) {
    audit.before = taskSnapshot(normalizedCurrent);
    audit.after = taskSnapshot(task);
  }
  return execution;
}

export function executeCompleteTask(
  state: TaskCommandState,
  input: TaskTransitionInput,
  context: CommandContext,
  deps: TaskCommandDeps,
): TaskCommandExecution {
  return executeTransition("completed", state, input, context, deps);
}

export function executeReopenTask(
  state: TaskCommandState,
  input: TaskTransitionInput,
  context: CommandContext,
  deps: TaskCommandDeps,
): TaskCommandExecution {
  return executeTransition("reopened", state, input, context, deps);
}

/**
 * Muda o andamento de uma tarefa pendente.
 *
 * Só pendente tem andamento: no quadro, "Concluídas" é a coluna de `status`, e
 * arrastar de volta para "Em andamento" é reabrir, não mudar andamento.
 */
export function executeSetTaskProgress(
  stateInput: TaskCommandState,
  input: TaskProgressInput,
  contextInput: CommandContext,
  deps: TaskCommandDeps,
): TaskCommandExecution {
  const context = checkedContext(contextInput);
  if (!context) return invalidContextFailure(stateInput);
  let parsed: TaskProgressInput;
  try {
    parsed = taskProgressSchema.parse(normalizeTaskJson(input));
  } catch (error) {
    return { ...validationFailure(context, error), state: stateInput };
  }
  const fingerprint = stableFingerprint({
    taskId: parsed.taskId,
    progress: parsed.progress,
    expectedVersion: context.expectedVersion,
  });
  const state = normalizeState(stateInput);
  const replay = recordLookup(state, context, "tasks.progress", fingerprint);
  if (replay) return replay;
  const current = state.tasks.find((item) => item.id === parsed.taskId && !item.deletedAt);
  if (!current)
    return { ...notFoundFailure(context, "Tarefa não encontrada."), state: stateInput };
  if (context.expectedVersion !== undefined && context.expectedVersion !== current.version)
    return {
      state: stateInput,
      result: commandError(context, "version_conflict", "A tarefa foi alterada por outra operação.", {
        severity: "warning",
      }),
    };
  if (current.status !== "pending")
    return {
      ...invalidStateFailure(context, "Somente tarefas pendentes têm andamento."),
      state: stateInput,
    };
  if (current.progress === parsed.progress)
    return {
      ...invalidStateFailure(context, "A tarefa já está neste andamento."),
      state: stateInput,
    };
  const now = deps.now();
  const task: Task = {
    ...current,
    progress: parsed.progress,
    updatedAt: now,
    version: current.version + 1,
  };
  const event = makeEvent(task, context, "progress_changed", now, deps.uuid());
  const execution = finish(
    state, context, "tasks.progress", fingerprint, task, event, "progress_changed",
    current.version, deps,
  );
  const audit = execution.state.auditLog.at(-1);
  if (audit) {
    audit.before = taskSnapshot(current);
    audit.after = taskSnapshot(task);
  }
  return execution;
}

/**
 * Edita descrição, prazo, prioridade e observação de uma tarefa.
 *
 * Pendente ou concluída, o conteúdo pode ser corrigido — o que não muda é o
 * cadastro vinculado e o desfecho. Editar registra evento próprio na jornada,
 * porque quem lê o histórico precisa saber que o texto de hoje não é o de
 * ontem; não conta como interação (§7.7).
 */
/**
 * Exclui uma tarefa, pendente ou concluída.
 *
 * Excluir é marca (`deletedAt`), não remoção: a jornada do cadastro ganha o
 * evento "Tarefa excluída" com a descrição, e o passado continua explicável
 * (§7.4). Tarefa excluída some de todas as listas e recusa qualquer outro
 * comando. Não conta como interação (§7.7).
 */
export function executeDeleteTask(
  stateInput: TaskCommandState,
  input: TaskTransitionInput,
  contextInput: CommandContext,
  deps: TaskCommandDeps,
): TaskCommandExecution {
  const context = checkedContext(contextInput);
  if (!context) return invalidContextFailure(stateInput);
  const taskId = typeof input?.taskId === "string" ? input.taskId.trim() : "";
  if (!taskId)
    return { ...invalidStateFailure(context, "Informe a tarefa."), state: stateInput };
  const fingerprint = stableFingerprint({ taskId, expectedVersion: context.expectedVersion });
  const state = normalizeState(stateInput);
  const replay = recordLookup(state, context, "tasks.delete", fingerprint);
  if (replay) return replay;
  const current = state.tasks.find((item) => item.id === taskId);
  if (!current || current.deletedAt)
    return { ...notFoundFailure(context, "Tarefa não encontrada."), state: stateInput };
  if (context.expectedVersion !== undefined && context.expectedVersion !== current.version)
    return {
      state: stateInput,
      result: commandError(context, "version_conflict", "A tarefa foi alterada por outra operação.", {
        severity: "warning",
      }),
    };
  const now = deps.now();
  const task: Task = { ...current, deletedAt: now, updatedAt: now, version: current.version + 1 };
  const event = makeEvent(task, context, "deleted", now, deps.uuid());
  return finish(state, context, "tasks.delete", fingerprint, task, event, "deleted", current.version, deps);
}

export function executeUpdateTask(
  stateInput: TaskCommandState,
  input: TaskEditInput,
  contextInput: CommandContext,
  deps: TaskCommandDeps,
): TaskCommandExecution {
  const context = checkedContext(contextInput);
  if (!context) return invalidContextFailure(stateInput);
  let parsed: TaskEditInput;
  try {
    parsed = taskEditSchema.parse(normalizeTaskJson(input));
  } catch (error) {
    return { ...validationFailure(context, error), state: stateInput };
  }
  const fingerprint = stableFingerprint({
    taskId: parsed.taskId,
    description: parsed.description,
    dueAt: parsed.dueAt,
    priority: parsed.priority,
    note: parsed.note ?? null,
    expectedVersion: context.expectedVersion,
  });
  const state = normalizeState(stateInput);
  const replay = recordLookup(state, context, "tasks.update", fingerprint);
  if (replay) return replay;
  const current = state.tasks.find((item) => item.id === parsed.taskId && !item.deletedAt);
  if (!current)
    return { ...notFoundFailure(context, "Tarefa não encontrada."), state: stateInput };
  if (context.expectedVersion !== undefined && context.expectedVersion !== current.version)
    return {
      state: stateInput,
      result: commandError(context, "version_conflict", "A tarefa foi alterada por outra operação.", {
        severity: "warning",
      }),
    };
  const igual =
    current.description === parsed.description &&
    current.dueAt === parsed.dueAt &&
    current.priority === parsed.priority &&
    (current.note ?? undefined) === (parsed.note || undefined);
  if (igual)
    return { ...invalidStateFailure(context, "Nada mudou nesta tarefa."), state: stateInput };

  const now = deps.now();
  const task: Task = {
    ...current,
    description: parsed.description,
    dueAt: parsed.dueAt,
    priority: parsed.priority,
    note: parsed.note || undefined,
    updatedAt: now,
    version: current.version + 1,
  };
  const event = makeEvent(task, context, "edited", now, deps.uuid());
  const execution = finish(
    state, context, "tasks.update", fingerprint, task, event, "edited",
    current.version, deps,
  );
  const audit = execution.state.auditLog.at(-1);
  if (audit) {
    audit.before = taskSnapshot(current);
    audit.after = taskSnapshot(task);
  }
  return execution;
}

export interface TaskDemoAdapter {
  create(input: TaskDraftInput, context: CommandContext): Promise<CommandResult<TaskCommandValue>>;
  complete(taskId: string, context: CommandContext): Promise<CommandResult<TaskCommandValue>>;
  reopen(taskId: string, context: CommandContext): Promise<CommandResult<TaskCommandValue>>;
  setProgress(
    input: TaskProgressInput,
    context: CommandContext,
  ): Promise<CommandResult<TaskCommandValue>>;
  update(input: TaskEditInput, context: CommandContext): Promise<CommandResult<TaskCommandValue>>;
  remove(taskId: string, context: CommandContext): Promise<CommandResult<TaskCommandValue>>;
  getState(): TaskCommandState;
  syncExternal(state: TaskCommandState): void;
}

export function createTaskDemoAdapter(
  initialState: TaskCommandState,
  deps: TaskCommandDeps,
  onCommit?: (state: TaskCommandState) => void,
): TaskDemoAdapter {
  let state = normalizeState(initialState);
  let queue = Promise.resolve();
  let busy = false;

  const enqueue = (
    operation: () => TaskCommandExecution,
  ): Promise<CommandResult<TaskCommandValue>> => {
    const run = () => {
      busy = true;
      const execution = operation();
      if (execution.result.ok && execution.result.status === "applied") {
        state = execution.state;
        onCommit?.(state);
      }
      busy = false;
      return execution.result;
    };
    if (!busy) return Promise.resolve(run());
    const next = queue.then(run);
    queue = next.then(() => undefined, () => undefined);
    return next;
  };

  return {
    create(input, context) {
      return enqueue(() => executeCreateTask(state, input, context, deps));
    },
    complete(taskId, context) {
      return enqueue(() => executeCompleteTask(state, { taskId }, context, deps));
    },
    reopen(taskId, context) {
      return enqueue(() => executeReopenTask(state, { taskId }, context, deps));
    },
    setProgress(input, context) {
      return enqueue(() => executeSetTaskProgress(state, input, context, deps));
    },
    update(input, context) {
      return enqueue(() => executeUpdateTask(state, input, context, deps));
    },
    remove(taskId, context) {
      return enqueue(() => executeDeleteTask(state, { taskId }, context, deps));
    },
    getState() {
      return state;
    },
    syncExternal(nextState) {
      if (!busy) state = normalizeState(nextState);
    },
  };
}
